import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, Clock, MessageSquare, Share2, ArrowLeft, Loader2, Mail, Facebook, Twitter, MessageCircle } from 'lucide-react';
import SectionTitle from './SectionTitle';
import Button from './Button';
import config from '../utils/config';

// Helpers
const formatDate = (value) => {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const getImageSrc = (poem) => {
  if (!poem) return '';
  if (poem.image_url) return poem.image_url;
  if (poem.coverUrl) return poem.coverUrl;
  if (poem.image) {
    const img = String(poem.image);
    if (/^https?:\/\//i.test(img)) return img;
    return `${config.url.replace(/\/$/, '')}/${img.replace(/^\//, '')}`;
  }
  if (poem.featured_image) {
    const img = String(poem.featured_image);
    if (/^https?:\/\//i.test(img)) return img;
    return `${config.url.replace(/\/$/, '')}/${img.replace(/^\//, '')}`;
  }
  return '';
};

const RachnayeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rachna, setRachna] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [showShareOptions, setShowShareOptions] = useState(false);
  const shareRef = useRef(null);

  // Helper functions for localStorage
  const getStorageKey = (type) => `rachnaye_${id}_${type}`;
  
  const saveToStorage = (type, data) => {
    try {
      localStorage.setItem(getStorageKey(type), JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  };
  
  const getFromStorage = (type, defaultValue = null) => {
    try {
      const stored = localStorage.getItem(getStorageKey(type));
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
      return defaultValue;
    }
  };

  // Save comment to database
  const saveCommentToDatabase = async (commentData) => {
    try {
      const response = await fetch(`${config.url}/rachnaye/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_name: commentData.user_name,
          user_email: commentData.user_email || '',
          comment: commentData.comment
        })
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to save comment');
      }
      
      // Add the new comment to the local state
      setComments(prevComments => [responseData.comment, ...prevComments]);
      return true;
    } catch (error) {
      console.error('Error saving comment:', error);
      setFormError(error.message || 'टिप्पणी सेव करने में समस्या आई है।');
      return false;
    }
  };

  // Fetch comments from backend
  const fetchComments = async () => {
    try {
      const response = await fetch(`${config.url}/rachnaye/${id}/comments`);
      if (!response.ok) throw new Error('Failed to fetch comments');
      const data = await response.json();
      setComments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.warn('Failed to fetch comments:', error);
      // Fallback to empty array if fetch fails
      setComments([]);
    }
  };

  // Handle comment submission
  const handleComment = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    
    // Basic validation
    if (!userName.trim()) {
      setFormError('कृपया अपना नाम दर्ज करें');
      return;
    }
    
    if (!userEmail.trim()) {
      setFormError('कृपया अपना ईमेल दर्ज करें');
      return;
    }
    
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail.trim());
    if (!emailValid) {
      setFormError('कृपया एक मान्य ईमेल पता दर्ज करें');
      return;
    }
    
    if (!newComment.trim()) {
      setFormError('कृपया कोई टिप्पणी दर्ज करें');
      return;
    }

    setCommentLoading(true);
    
    const commentData = {
      user_name: userName.trim(),
      user_email: userEmail.trim(),
      comment: newComment.trim()
    };
    
    try {
      const success = await saveCommentToDatabase(commentData);
      if (success) {
        setNewComment('');
        setFormError('');
        setFormSuccess('आपकी टिप्पणी सफलतापूर्वक जमा कर दी गई है!');
        
        // Save user info for future comments
        localStorage.setItem('comment_user_name', userName.trim());
        localStorage.setItem('comment_user_email', userEmail.trim().toLowerCase());
        
        // Clear success message after 5 seconds
        setTimeout(() => setFormSuccess(''), 5000);
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      setFormError(error.message || 'टिप्पणी जमा करने में त्रुटि हुई');
    } finally {
      setCommentLoading(false);
    }
  };

  // Fetch rachna details and comments
  const fetchRachna = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.url}/rachnaye`);
      if (!response.ok) throw new Error('Failed to fetch rachnaye');
      const data = await response.json();
      const found = data.find(p => String(p.id) === String(id));
      if (!found) throw new Error('रचना नहीं मिली');
      
      setRachna(found);
      
      // Load comments from database (authoritative)
      await fetchComments();
      
      // Load saved user info if available
      const savedName = localStorage.getItem('comment_user_name');
      const savedEmail = localStorage.getItem('comment_user_email');
      if (savedName) setUserName(savedName);
      if (savedEmail) setUserEmail(savedEmail);
      
    } catch (err) {
      setError('रचना डेटा लोड करने में समस्या आई है।');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchRachna();
    
    // Set up click outside for share options
    const handleClickOutside = (event) => {
      if (shareRef.current && !shareRef.current.contains(event.target)) {
        setShowShareOptions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [id]);

  if (loading) return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 text-center">
        <SectionTitle>रचना विवरण</SectionTitle>
        <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
        <p className="text-lg text-gray-600">रचना लोड हो रही है...</p>
      </div>
    </section>
  );

  if (error || !rachna) return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 text-center">
        <SectionTitle>रचना विवरण</SectionTitle>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md mx-auto">
          <p className="text-lg">{error}</p>
        </div>
        <div className="mt-4">
          <Button to="/rachnaye" variant="outline" leftIcon={ArrowLeft}>
            सभी रचनाएँ पर वापस जाएं
          </Button>
        </div>
      </div>
    </section>
  );

  return (
    <section className="py-12 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-8">
              <div className="mb-6">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{rachna.title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-gray-600">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-orange-500" />
                    <span>{formatDate(rachna.date)}</span>
                  </div>
                  {rachna.author && (
                    <div className="flex items-center">
                      <span className="font-semibold">लेखक: {rachna.author}</span>
                    </div>
                  )}
                </div>
              </div>

              {rachna.description && (
                <div className="mb-8">
                  <div className="prose prose-lg max-w-none">
                    <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-wrap">{rachna.description}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Share Section */}
            <div className="border-t bg-gray-50 p-6">
              <div className="flex items-center justify-end mb-6">
                <div className="relative" ref={shareRef}>
                  <button
                    onClick={() => setShowShareOptions(!showShareOptions)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <Share2 size={16} />
                    <span>शेयर करें</span>
                  </button>
                  
                  {showShareOptions && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-10">
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-4 py-2 text-gray-800 hover:bg-gray-100"
                      >
                        <Facebook className="w-4 h-4 mr-2 text-blue-600" />
                        Facebook
                      </a>
                      <a
                        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-4 py-2 text-gray-800 hover:bg-gray-100"
                      >
                        <Twitter className="w-4 h-4 mr-2 text-blue-400" />
                        Twitter
                      </a>
                      <a
                        href={`whatsapp://send?text=${encodeURIComponent(window.location.href)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-4 py-2 text-gray-800 hover:bg-gray-100"
                      >
                        <MessageCircle className="w-4 h-4 mr-2 text-green-500" />
                        WhatsApp
                      </a>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Comments Section */}
              <div className="mt-12 max-w-4xl mx-auto scroll-mt-20" id="comments">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-gray-800">
                  <MessageSquare className="w-6 h-6" />
                  टिप्पणियाँ ({comments.length})
                </h2>
                
                {/* Add Comment Form */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800">अपनी टिप्पणी साझा करें</h3>
                  
                  {formError && (
                    <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                      {formError}
                    </div>
                  )}
                  
                  {formSuccess && (
                    <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md text-sm">
                      {formSuccess}
                    </div>
                  )}
                  
                  <form onSubmit={handleComment}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="name"
                          value={userName}
                          onChange={(e) => setUserName(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="आपका नाम"
                        />
                      </div>
                      
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="email"
                          id="email"
                          value={userEmail}
                          onChange={(e) => setUserEmail(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="आपका ईमेल"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="comment" className="sr-only">आपकी टिप्पणी</label>
                      <textarea
                        id="comment"
                        rows="4"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="अपनी टिप्पणी यहाँ लिखें..."
                      ></textarea>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={!userName.trim() || !userEmail.trim() || !newComment.trim() || commentLoading}
                      className="w-full md:w-auto px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-md transition duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {commentLoading ? (
                        <>
                          <Loader2 className="animate-spin mr-2 h-4 w-4" />
                          जमा किया जा रहा है...
                        </>
                      ) : (
                        'टिप्पणी जमा करें'
                      )}
                    </button>
                  </form>
                </div>
                
                {/* Comments List */}
                {comments && comments.length > 0 ? (
                  <div className="mt-6 space-y-4">
                    {comments.map((comment) => (
                      <div key={comment?.id || Math.random().toString(36).substr(2, 9)} className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                              <User className="h-5 w-5" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-gray-800">
                                {comment?.user_name || 'Anonymous'}
                              </h4>
                              <span className="text-xs text-gray-500">
                                {comment?.created_at ? (
                                  new Date(comment.created_at).toLocaleDateString('hi-IN', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                ) : ''}
                              </span>
                            </div>
                            <p className="mt-1 text-gray-700">{comment?.comment || ''}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    कोई टिप्पणी नहीं है। सबसे पहले टिप्पणी करें!
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <Button to="/rachnaye" size="sm" variant="outline" leftIcon={ArrowLeft}>
            सभी रचनाएँ पर वापस जाएं
          </Button>
        </div>
      </div>
    </section>
  );
};

export default RachnayeDetailPage;